/**
 * MoonHubCompletionView
 *
 * Treatment summary when all steps are complete. EMBER glass-styled.
 * Warm gold/copper confetti, glass card summary, earth-tone accents.
 */

import { useEffect, useRef } from "react";
import { View, Image, Animated as RNAnimated } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import {
  Check,
  ChevronRight,
  ClipboardCheck,
  PartyPopper,
  Pencil,
  Pill,
  Zap,
  SkipForward,
} from "lucide-react-native";
import type {
  TreatmentStepProgress,
  LocalMedication,
} from "@louise/care/treatment-phases";
import type { TreatmentStepConfig } from "@src/packages/homepage/sections/TreatmentProgressModal";
import { useTranslations } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import type { Locale, TranslationFunction } from "@src/packages/i18n";
import { parseDateOrTimestamp } from "@src/packages/utils/date";
import { EMBER } from "../../../../../onboarding/theme";
import { colors, semanticColors } from "@louise/theme/tokens";
import StatusBadge from "../../../../components/StatusBadge";
import { CompactActionButton, IconButton, Row } from "@louise/moon-ui";

const CRYO_VAULT_ILLUSTRATION =
  require("@src/apps/moon/assets/images/cryo-vault-fridge.png") as number;

const TEXT = semanticColors.text.primary;
const TEXT_MID = semanticColors.text.secondary;
const TEXT_LIGHT = semanticColors.text.tertiary;
const TEXT_FAINT = semanticColors.text.faint;
const BORDER = semanticColors.border.subtle;
const SUMMARY_BG = semanticColors.surface.subtle;

interface MoonHubCompletionViewProps {
  /**
   * `panel` replaces the hub's tab strip with the celebration, a summary list of
   * every step and the CTA that moves the cycle on. `banner` is the celebration
   * alone, sitting above a tab strip that stays where it is.
   *
   * The summary list and that CTA only ever existed because this view took the
   * whole card: with the strip gone, the list was the only way left to see or
   * edit a step, and "Enter waiting phase" led back to a wait that had no tab of
   * its own. Flows that end in a result have an Outcome tab now, so both are
   * obsolete for them — and visibly so, since the list would say "Outcome —
   * Completed" directly above a button offering to start the wait.
   *
   * Preservation flows have no such tab, so they keep the panel: for them this
   * is still the terminal surface, and the Cryo Vault bridge and the
   * complete-cycle CTA are real actions rather than workarounds.
   */
  presentation?: "panel" | "banner";
  steps: TreatmentStepConfig[];
  stepProgress: TreatmentStepProgress[] | null;
  stepMedications: Record<string, LocalMedication[]>;
  treatmentName: string;
  clinicName: string | null;
  cycleDay: number | null;
  cycleNumber: number;
  goal: "pregnancy" | "preserve";
  canEnterWaiting?: boolean;
  hasSpecimens?: boolean;
  /**
   * Cycle type — used to label the Cryo Vault bridge appropriately
   * (e.g. "Add the frozen sperm to your Cryo Vault" for sperm_freezing).
   */
  cycleType?: string;
  onComplete?: () => void;
  onStartEditing?: (stepId: string) => void;
  /**
   * Steps whose record belongs to the clinic, which the recap lists without an
   * Edit — the panel locks them for the same reason and the two views have to
   * agree, or the recap becomes the way around the lock.
   */
  clinicRecordedStepIds?: readonly string[];
  /** When set (preservation flows), renders a card linking to the Cryo Vault. */
  onOpenCryoVault?: () => void;
}

function formatDate(
  isoString: string | null | undefined,
  locale: Locale,
): string | null {
  if (!isoString || typeof isoString !== "string") return null;
  try {
    const d = parseDateOrTimestamp(isoString);
    if (isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
    }).format(d);
  } catch {
    return null;
  }
}

function getStepSummary(
  step: TreatmentStepConfig,
  sp: TreatmentStepProgress,
  meds: LocalMedication[],
  t: TranslationFunction,
  locale: Locale,
): string {
  const data = sp.data ?? {};

  if (step.contentType === "medications") {
    if (meds.length === 0) return t("care.treatment_hub.completion.completed");
    const names = meds
      .slice(0, 3)
      .map((m) => m.name || "Unknown")
      .join(", ");
    const suffix =
      meds.length > 3
        ? `, ${t("care.treatment_hub.completion.med_more_suffix", {
            params: { count: String(meds.length - 3) },
          })}`
        : "";
    const medCountKey =
      meds.length === 1
        ? "care.treatment_hub.completion.med_count_one"
        : "care.treatment_hub.completion.med_count_other";
    const medCountLabel = t(medCountKey, {
      params: { count: String(meds.length) },
    });
    return `${medCountLabel} · ${names}${suffix}`;
  }

  if (step.contentType === "trigger" || step.id === "trigger_shot") {
    const triggerDate = formatDate(
      (data.triggerDate as string) ?? (data.triggerTime as string),
      locale,
    );
    const med = data.triggerMedication;
    const parts: string[] = [
      t("care.treatment_hub.completion.trigger_confirmed"),
    ];
    if (med) parts.push(String(med));
    if (triggerDate) parts.push(triggerDate);
    return parts.join(" · ");
  }

  const parts: string[] = [];

  if (step.id === "egg_retrieval") {
    if (data.eggsCollected != null) {
      parts.push(
        t("care.treatment_hub.completion.eggs_collected", {
          params: { count: String(data.eggsCollected) },
        }),
      );
    }
    const date = formatDate(data.procedureDate as string, locale);
    if (date) parts.push(date);
  } else if (step.id === "freezing") {
    if (data.matureEggs != null) {
      parts.push(
        t("care.treatment_hub.completion.mature_count", {
          params: { count: String(data.matureEggs) },
        }),
      );
    }
    if (data.eggsFrozen != null) {
      parts.push(
        t("care.treatment_hub.completion.frozen_count", {
          params: { count: String(data.eggsFrozen) },
        }),
      );
    }
  } else if (step.id === "fertilization") {
    if (data.eggsFertilized != null) {
      parts.push(
        t("care.treatment_hub.completion.fertilized_count", {
          params: { count: String(data.eggsFertilized) },
        }),
      );
    }
    if (data.fertilizationMethod) {
      parts.push(String(data.fertilizationMethod).toUpperCase());
    }
  } else if (step.id === "transfer") {
    if (data.embryosTransferred != null) {
      parts.push(
        t("care.treatment_hub.completion.transferred_count", {
          params: { count: String(data.embryosTransferred) },
        }),
      );
    }
    const date = formatDate(data.transferDate as string, locale);
    if (date) parts.push(date);
  } else if (step.id === "sperm_collection") {
    if (data.spermSource) parts.push(String(data.spermSource));
  } else if (step.id === "insemination") {
    const date = formatDate(data.procedureDate as string, locale);
    if (date) {
      return `${t("care.treatment_hub.completion.iui_completed")} · ${date}`;
    }
  } else if (step.fields) {
    for (const field of step.fields) {
      const val = data[field.name];
      if (val != null && val !== "" && field.type !== "textarea") {
        // The label no longer names the unit; the field carries it.
        const unit = field.unit
          ? field.unit === "%"
            ? "%"
            : ` ${field.unit}`
          : "";
        parts.push(`${field.label}: ${val}${unit}`);
      }
    }
  }

  return parts.length > 0
    ? parts.join(" · ")
    : t("care.treatment_hub.completion.completed");
}

function ConfettiParticle({
  delay,
  startX,
  color,
}: {
  delay: number;
  startX: number;
  color: string;
}) {
  const translateY = useRef(new RNAnimated.Value(-20)).current;
  const opacity = useRef(new RNAnimated.Value(0)).current;
  const rotate = useRef(new RNAnimated.Value(0)).current;

  useEffect(() => {
    const timer = setTimeout(() => {
      RNAnimated.parallel([
        RNAnimated.timing(translateY, {
          toValue: 80,
          duration: 1200,
          useNativeDriver: true,
        }),
        RNAnimated.sequence([
          RNAnimated.timing(opacity, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
          }),
          RNAnimated.timing(opacity, {
            toValue: 0,
            duration: 800,
            delay: 200,
            useNativeDriver: true,
          }),
        ]),
        RNAnimated.timing(rotate, {
          toValue: 360,
          duration: 1200,
          useNativeDriver: true,
        }),
      ]).start();
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, translateY, opacity, rotate]);

  return (
    <RNAnimated.View
      style={{
        position: "absolute",
        top: 0,
        width: 6,
        height: 6,
        borderRadius: 1,
        backgroundColor: color,
        opacity,
        transform: [
          { translateY },
          { translateX: startX },
          {
            rotate: rotate.interpolate({
              inputRange: [0, 360],
              outputRange: ["0deg", "360deg"],
            }),
          },
        ],
      }}
      pointerEvents="none"
    />
  );
}

function StepSummaryRow({
  step,
  sp,
  meds,
  onEdit,
  t,
  locale,
}: {
  step: TreatmentStepConfig;
  sp: TreatmentStepProgress;
  meds: LocalMedication[];
  onEdit?: () => void;
  t: TranslationFunction;
  locale: Locale;
}) {
  const isSkipped = sp.status === "skipped";
  const summary = isSkipped
    ? t("care.treatment_hub.completion.skipped")
    : getStepSummary(step, sp, meds, t, locale);

  const icon = (() => {
    if (isSkipped) {
      return <SkipForward size={13} color={TEXT_LIGHT} />;
    }
    if (step.id === "trigger_shot" || step.contentType === "trigger") {
      return <Zap size={13} color={TEXT_MID} />;
    }
    if (step.contentType === "medications") {
      return <Pill size={13} color={TEXT_MID} />;
    }
    return <Check size={13} color={TEXT_MID} strokeWidth={2.4} />;
  })();

  return (
    <View
      className="flex-row items-start gap-3 rounded-xl px-3.5 py-2.5"
      style={{
        backgroundColor: SUMMARY_BG,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <View
        style={{
          width: 20,
          height: 20,
          borderRadius: 10,
          alignItems: "center",
          justifyContent: "center",
          marginTop: 1,
        }}
      >
        {icon}
      </View>

      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "700",
            color: isSkipped ? TEXT_LIGHT : TEXT,
            marginBottom: 2,
          }}
        >
          {step.title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: isSkipped ? TEXT_FAINT : TEXT_LIGHT,
            lineHeight: 16,
          }}
          numberOfLines={2}
        >
          {summary}
        </Text>
      </View>

      {onEdit && (
        <IconButton
          accessibilityLabel={t(
            "care.treatment_hub.completion.edit_step_summary",
            {
              params: { step: step.title },
            },
          )}
          action="edit_step_summary"
          onPress={onEdit}
          hitSlop={8}
          size={24}
          surface="frosted"
          style={{ marginTop: 1 }}
        >
          <Pencil size={10} color={TEXT_LIGHT} />
        </IconButton>
      )}
    </View>
  );
}

export function MoonHubCompletionView({
  steps,
  stepProgress,
  stepMedications,
  treatmentName,
  presentation = "panel",
  clinicName,
  cycleDay,
  cycleNumber,
  goal,
  canEnterWaiting = true,
  hasSpecimens = false,
  cycleType,
  onComplete,
  onStartEditing,
  clinicRecordedStepIds,
  onOpenCryoVault,
}: MoonHubCompletionViewProps) {
  const t = useTranslations();
  const { locale } = useLocale();
  const reducedMotion = useReducedMotion();

  const confettiColors = [
    colors.biologyFrost,
    colors.biologyAmber,
    colors.biologyCopper,
    colors.biologyBrown,
    EMBER.colors.textFaint,
  ];

  const ctaText =
    goal === "preserve"
      ? hasSpecimens
        ? t("care.treatment_hub.completion.cta_complete_cycle")
        : t("care.treatment_hub.completion.cta_complete_preservation")
      : canEnterWaiting
        ? t("care.treatment_hub.completion.cta_enter_waiting")
        : t("care.treatment_hub.completion.cta_complete_cycle");

  const cycleLabel = t("care.treatment_hub.completion.cycle_label", {
    params: { treatment: treatmentName, number: String(cycleNumber) },
  });

  const subLine = (() => {
    if (cycleDay != null && clinicName) {
      return t("care.treatment_hub.completion.days_clinic_separator", {
        params: { days: String(cycleDay), clinic: clinicName },
      });
    }
    if (cycleDay != null) {
      return t("care.treatment_hub.completion.days_only", {
        params: { days: String(cycleDay) },
      });
    }
    return clinicName ?? "";
  })();

  const cryoTitle =
    cycleType === "sperm_freezing"
      ? t("care.treatment_hub.completion.cryo_add_default")
      : cycleType === "egg_freezing"
        ? t("care.treatment_hub.completion.cryo_add_eggs")
        : cycleType === "embryo_freezing"
          ? t("care.treatment_hub.completion.cryo_add_embryos")
          : t("care.treatment_hub.completion.cryo_add_default");

  const isBanner = presentation === "banner";

  return (
    <View
      style={
        isBanner
          ? { paddingTop: 16, paddingBottom: 18, paddingHorizontal: 20 }
          : { padding: 20, gap: 16 }
      }
    >
      {/* The burst ends fully transparent, so with motion reduced its end
          state is no confetti at all — not fifteen invisible layers left
          composited over the heading. */}
      {!reducedMotion && (
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 100,
            overflow: "hidden",
          }}
          pointerEvents="none"
        >
          {confettiColors.map((color, i) =>
            Array.from({ length: 3 }).map((_, j) => (
              <ConfettiParticle
                key={`${i}-${j}`}
                delay={i * 80 + j * 150}
                startX={30 + (((i * 3 + j) * 25) % 260)}
                color={color}
              />
            )),
          )}
        </View>
      )}

      <View style={{ alignItems: "center", gap: 8 }}>
        <PartyPopper size={28} color={TEXT_MID} strokeWidth={1.8} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 18,
            fontWeight: "800",
            color: TEXT,
          }}
        >
          {/* The banner fires on the treatment being behind her, which is
              usually true with steps still unlogged — so claiming they were all
              complete stated something she could see was false.

              The `all_steps_complete` half is unreachable: the panel
              presentation has no call site left, and neither do
              `StepSummaryRow`, `CryoVaultBridgeButton`, `onComplete`,
              `onStartEditing`, `canEnterWaiting`, `hasSpecimens` or `ctaText`.
              Deleting that surface is a cleanup of its own, deliberately not
              folded into this fix. */}
          {t(
            isBanner
              ? "care.treatment_hub.completion.treatment_complete"
              : "care.treatment_hub.completion.all_steps_complete",
          )}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            fontWeight: "600",
            color: TEXT_MID,
            textAlign: "center",
          }}
        >
          {cycleLabel}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: TEXT_FAINT,
            textAlign: "center",
          }}
        >
          {subLine}
        </Text>
      </View>

      {isBanner ? null : (
        <View style={{ gap: 6 }}>
          <View style={{ marginBottom: 4, alignSelf: "flex-start" }}>
            <StatusBadge
              label={t("care.treatment_hub.completion.summary_header")}
              tone="neutral"
              icon={ClipboardCheck}
            />
          </View>
          {steps.map((step) => {
            const sp = stepProgress?.find((s) => s.stepId === step.id);
            if (!sp || sp.status === "pending") return null;
            const meds = stepMedications[step.id] ?? [];

            return (
              <StepSummaryRow
                key={step.id}
                step={step}
                sp={sp}
                meds={meds}
                onEdit={
                  onStartEditing && !clinicRecordedStepIds?.includes(step.id)
                    ? () => onStartEditing(step.id)
                    : undefined
                }
                t={t}
                locale={locale}
              />
            );
          })}
        </View>
      )}

      {/* Cryo Vault bridge — preservation flows only. Lets the user enter
          full specimen details (vial counts, storage facility, etc.) on the
          canonical Cryo Vault surface rather than duplicating fields here.
          When specimens are already saved, switches to a confirmation state
          that still links back to the vault for review. */}
      {!isBanner && onOpenCryoVault && goal === "preserve" && (
        <Row
          surface="glass"
          leading={
            <Image
              source={CRYO_VAULT_ILLUSTRATION}
              resizeMode="contain"
              style={{ width: 40, height: 40 }}
            />
          }
          leadingSize={40}
          action="cryo_vault_bridge"
          title={
            hasSpecimens
              ? t("care.treatment_hub.completion.cryo_added_title")
              : cryoTitle
          }
          subtitle={
            hasSpecimens
              ? t("care.treatment_hub.completion.cryo_added_subtitle")
              : t("care.treatment_hub.completion.cryo_add_subtitle")
          }
          accessibilityLabel={
            hasSpecimens
              ? t("care.treatment_hub.completion.cryo_added_a11y")
              : cryoTitle
          }
          onPress={onOpenCryoVault}
        />
      )}

      {!isBanner && onComplete ? (
        <CompactActionButton
          label={
            goal === "preserve"
              ? t("care.treatment_hub.completion.cta_complete_this_cycle")
              : ctaText
          }
          onPress={onComplete}
          trailingIcon={ChevronRight}
          variant="primary"
          minWidth={168}
        />
      ) : null}
    </View>
  );
}
